import { useAuth } from '../contexts/AuthContext';
import {
  Home, LogOut, Building2, Settings, CheckSquare, BarChart,
  Trophy, Clock, FileSpreadsheet, Calculator, TrendingUp,
  Menu, X, User, ChevronRight, ChevronLeft, ChevronLast, Bell, RefreshCw
} from 'lucide-react';
import { Link, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../hooks/useNotifications';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() =>
    localStorage.getItem('sidebar-collapsed') === 'true'
  );
  const { unreadCount } = useNotifications();

  const toggleSidebar = () => {
    setIsCollapsed(prev => {
      localStorage.setItem('sidebar-collapsed', String(!prev));
      return !prev;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const [refreshing, setRefreshing] = useState(false);
  const handleCacheRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      // Vider tous les caches du service worker
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      // Demander au service worker de se mettre à jour
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
      window.location.reload();
    } catch {
      window.location.reload();
    }
  }, [refreshing]);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const isAdmin = user?.role === 'admin';
  const isDirector = user?.role === 'direction';
  const isResponsable = user?.role === 'responsable';
  const isUser = user?.role === 'user';
  const isReadOnly = user?.role === 'lecture';

  const allMenuItems = [
    { path: '/',                      icon: Home,            label: 'Accueil',           show: true },
    { path: '/validation',            icon: CheckSquare,     label: 'Validation',        show: isAdmin },
    { path: '/pending',               icon: Clock,           label: 'En cours',          show: isAdmin || isReadOnly },
    { path: '/regul',                 icon: FileSpreadsheet, label: 'Régularisation',    show: isAdmin },
    { path: '/statistics',            icon: BarChart,        label: 'Statistiques',      show: isAdmin || isDirector },
    { path: '/performance',           icon: TrendingUp,      label: 'Performance',       show: isAdmin },
    { path: '/reporting',             icon: FileSpreadsheet, label: 'Reporting',         show: isAdmin },
    { path: '/top-position',          icon: Trophy,          label: 'Classement',        show: isAdmin || isDirector || isReadOnly },
    { path: '/accounting-objectives', icon: Calculator,      label: 'Obj. Comptables',   show: isAdmin },
    { path: '/reporting-agence',      icon: Building2,       label: 'Reporting Agence',  show: isUser || isResponsable || isDirector || isAdmin },
    { path: '/settings',              icon: Settings,        label: 'Paramètres',        show: isAdmin },
  ].filter(i => i.show);

  const getBottomNavItems = () => {
    if (isAdmin) {
      return [
        { path: '/',            icon: Home,            label: 'Accueil' },
        { path: '/validation',  icon: CheckSquare,     label: 'Validation' },
        { path: '/statistics',  icon: BarChart,        label: 'Stats' },
        { path: '/reporting',   icon: FileSpreadsheet, label: 'Reporting' },
      ];
    }
    if (isReadOnly) {
      return [
        { path: '/',             icon: Home,   label: 'Accueil' },
        { path: '/pending',      icon: Clock,  label: 'En cours' },
        { path: '/top-position', icon: Trophy, label: 'Classement' },
      ];
    }
    if (isDirector) {
      return [
        { path: '/',                 icon: Home,      label: 'Accueil' },
        { path: '/statistics',       icon: BarChart,  label: 'Stats' },
        { path: '/top-position',     icon: Trophy,    label: 'Classement' },
        { path: '/reporting-agence', icon: Building2, label: 'Agence' },
      ];
    }
    return [
      { path: '/',                 icon: Home,      label: 'Accueil' },
      { path: '/reporting-agence', icon: Building2, label: 'Agence' },
      { path: '/profile',          icon: User,      label: 'Profil' },
    ];
  };

  const bottomNavItems = getBottomNavItems();
  const initials = `${user?.firstname?.[0] ?? ''}${user?.lastname?.[0] ?? ''}`;

  /* ── Sidebar content (réutilisé desktop + drawer) ── */
  const SidebarContent = ({ onClose }: { onClose?: () => void }) => (
    <>
      {/* Logo */}
      <div className={`flex items-center gap-3 ${isCollapsed && !onClose ? 'px-3 pt-6 pb-3 flex-col' : 'px-5 pt-6 pb-4'}`}>
        {(!isCollapsed || onClose) && (
          <>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', boxShadow: '0 0 16px rgba(99,102,241,0.4)' }}>
              <Building2 size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white tracking-wider">K-PROD</p>
              <p className="text-[10px] text-slate-500 font-medium tracking-wide">Gestion transactions</p>
            </div>
            {onClose ? (
              <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-colors">
                <X size={18} />
              </button>
            ) : (
              <button onClick={toggleSidebar} title="Réduire"
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-colors shrink-0">
                <ChevronLeft size={16} />
              </button>
            )}
          </>
        )}
        {isCollapsed && !onClose && (
          <>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', boxShadow: '0 0 16px rgba(99,102,241,0.35)' }}>
              <Building2 size={18} className="text-white" />
            </div>
            <button onClick={toggleSidebar} title="Agrandir"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-300 hover:bg-white/[0.08] transition-colors">
              <ChevronRight size={15} />
            </button>
          </>
        )}
      </div>

      {/* Séparateur */}
      <div className="mx-4 mb-3 h-px bg-white/[0.06]" />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5 scrollbar-none">
        {(!isCollapsed || onClose) && (
          <p className="px-2 mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600">
            Navigation
          </p>
        )}
        {allMenuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              title={isCollapsed && !onClose ? item.label : undefined}
              className={`flex items-center rounded-xl transition-all duration-150 group ${
                isCollapsed && !onClose ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'
              } ${
                active
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.07]'
              }`}
              style={active ? { boxShadow: '0 4px 14px rgba(99,102,241,0.35)' } : {}}
            >
              <div className={`shrink-0 rounded-lg transition-all ${
                active
                  ? 'text-white'
                  : 'text-slate-500 group-hover:text-slate-200'
              } ${isCollapsed && !onClose ? '' : 'w-[18px]'}`}>
                <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
              </div>
              {(!isCollapsed || onClose) && (
                <span className="text-sm font-medium truncate flex-1">{item.label}</span>
              )}
              {(!isCollapsed || onClose) && active && (
                <div className="w-1.5 h-1.5 rounded-full bg-white/60 shrink-0" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer utilisateur */}
      <div className="mx-3 mb-4 mt-2">
        <div className="h-px bg-white/[0.06] mb-3" />

        {/* Profil + bouton refresh */}
        <div className={`flex items-center rounded-xl mb-1.5 ${isCollapsed && !onClose ? 'justify-center' : 'gap-2'}`}>
          <NavLink
            to="/profile"
            title={isCollapsed && !onClose ? `${user?.firstname} ${user?.lastname}` : undefined}
            className={`flex items-center flex-1 rounded-xl transition-all hover:bg-white/[0.07] ${
              isCollapsed && !onClose ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'
            }`}
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 0 10px rgba(99,102,241,0.3)' }}>
              {initials}
            </div>
            {(!isCollapsed || onClose) && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-200 truncate leading-tight">
                  {user?.firstname} {user?.lastname}
                </p>
                <p className="text-[11px] text-slate-500 capitalize leading-tight mt-0.5">{user?.role}</p>
              </div>
            )}
          </NavLink>
          {(!isCollapsed || onClose) && (
            <button
              onClick={handleCacheRefresh}
              disabled={refreshing}
              title="Actualiser l'application"
              className="p-2 rounded-lg text-slate-500 hover:text-indigo-400 hover:bg-white/[0.07] transition-colors shrink-0 disabled:opacity-40"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          title={isCollapsed && !onClose ? 'Déconnexion' : undefined}
          className={`w-full flex items-center rounded-xl transition-all text-slate-500 hover:text-red-400 hover:bg-red-500/[0.08] ${
            isCollapsed && !onClose ? 'justify-center p-2.5' : 'gap-3 px-3 py-2.5'
          }`}
        >
          <LogOut size={17} strokeWidth={1.8} className="shrink-0" />
          {(!isCollapsed || onClose) && (
            <span className="text-sm font-medium">Déconnexion</span>
          )}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ backgroundColor: 'var(--color-bg)' }}>

      {/* ─── DESKTOP : sidebar fixe ─────────────────────────────────── */}
      <aside
        className="hidden md:flex md:flex-col md:fixed md:left-0 md:top-0 md:bottom-0 z-40 overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #080e1c 0%, #0d1322 55%, #0e1230 100%)',
          borderRight: '1px solid rgba(255,255,255,0.04)',
          boxShadow: '4px 0 32px rgba(0,0,0,0.35)',
          width: isCollapsed ? '4rem' : '17rem',
          transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <SidebarContent />
      </aside>

      {/* ─── MOBILE : header haut ────────────────────────────────────── */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 z-50"
        style={{
          background: 'linear-gradient(90deg, #080e1c 0%, #0e1230 100%)',
          paddingTop: 'env(safe-area-inset-top)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        }}
      >
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-200 hover:bg-white/[0.07] transition-colors touch-manipulation"
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>

          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', boxShadow: '0 0 12px rgba(99,102,241,0.4)' }}>
              <Building2 size={14} className="text-white" />
            </div>
            <span className="font-bold text-white text-sm tracking-wider">K-PROD</span>
          </div>

          <NavLink
            to="/profile"
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
          >
            {initials}
          </NavLink>
        </div>
      </header>

      {/* ─── MOBILE : overlay drawer ─────────────────────────────────── */}
      {isDrawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 backdrop-blur-sm"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* ─── MOBILE : drawer latéral ─────────────────────────────────── */}
      <div
        className="md:hidden fixed top-0 left-0 bottom-0 w-72 z-50 flex flex-col transform transition-transform duration-300 ease-in-out"
        style={{
          background: 'linear-gradient(160deg, #080e1c 0%, #0d1322 55%, #0e1230 100%)',
          borderRight: '1px solid rgba(255,255,255,0.04)',
          boxShadow: '20px 0 60px rgba(0,0,0,0.5)',
          transform: isDrawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <SidebarContent onClose={() => setIsDrawerOpen(false)} />
      </div>

      {/* ─── CONTENU PRINCIPAL ───────────────────────────────────────── */}
      <main
        className={`flex-1 mt-14 md:mt-0 p-4 md:p-6 ${isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
        style={{
          paddingBottom: `max(5rem, calc(env(safe-area-inset-bottom) + 4.5rem))`,
          transition: 'margin-left 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {children}
      </main>

      {/* ─── MOBILE : barre de navigation en bas ─────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40"
        style={{
          background: 'linear-gradient(90deg, #080e1c 0%, #0e1230 100%)',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.3)',
        }}
      >
        <div className="flex items-stretch">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] transition-all touch-manipulation"
                style={{ color: active ? '#818cf8' : 'rgba(148,163,184,0.55)' }}
              >
                <div
                  className="p-1.5 rounded-xl transition-all"
                  style={active ? { backgroundColor: 'rgba(99,102,241,0.18)', boxShadow: '0 0 10px rgba(99,102,241,0.25)' } : {}}
                >
                  <Icon size={21} strokeWidth={active ? 2.2 : 1.8} />
                </div>
                <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
              </Link>
            );
          })}

          <button
            onClick={() => setIsDrawerOpen(true)}
            className="flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] transition-all touch-manipulation"
            style={{ color: 'rgba(148,163,184,0.55)' }}
          >
            <div className="p-1.5 rounded-xl">
              <Menu size={21} strokeWidth={1.8} />
            </div>
            <span className="text-[10px] mt-0.5 font-medium">Plus</span>
          </button>
        </div>
      </nav>

    </div>
  );
}
